// Bar models (Year 2 pictures, 22 Oct 2026): a part-part-whole bar, with the
// whole on top and two or three parts under it, or two bars to compare, with
// the gap between them. Each bar carries its number or "?". The number
// behind "?" is never written or spoken: finding it is the question.
//
// The bars are drawn to scale (31 Oct 2026; they used to keep a minimum
// width each, so 99 and 1 looked nearly the same). The parts fill the whole
// exactly. A number too long for its bar is printed just under the bar.
//
// Pure parsing and geometry only, no components (see
// practice-diagram-data.ts).

export type BarValue = number | '?';

export type PartWholeSpec = {
  type: 'bar_model';
  kind: 'part_whole';
  whole: BarValue;
  parts: BarValue[];
};

export type CompareBar = {
  // A short name printed beside the bar, such as "Ivy"; null for none.
  name: string | null;
  value: BarValue;
};

export type CompareSpec = {
  type: 'bar_model';
  kind: 'compare';
  // The longer bar always comes first, and is drawn on top.
  bars: [CompareBar, CompareBar];
  // The gap at the end of the shorter bar; null when it is not drawn.
  difference: BarValue | null;
};

export type BarModelSpec = PartWholeSpec | CompareSpec;

// Year 2 numbers go to 1000.
const MAX_VALUE = 1000;
const MAX_NAME = 12;

function isWholeIn(value: unknown, low: number, high: number): boolean {
  return (
    typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= low &&
    value <= high
  );
}

function readValue(value: unknown): BarValue | null {
  if (value === '?') return '?';
  return isWholeIn(value, 1, MAX_VALUE) ? (value as number) : null;
}

// A name a child can read: letters, spaces, hyphens and apostrophes only.
function readName(value: unknown): string | null | undefined {
  if (value === undefined) return null;
  if (typeof value !== 'string') return undefined;
  const name = value.trim();
  if (name.length < 1 || name.length > MAX_NAME) return undefined;
  return /^[A-Za-z][A-Za-z '-]*$/.test(name) ? name : undefined;
}

function unknowns(values: (BarValue | null)[]): number {
  return values.filter((value) => value === '?').length;
}

// The number behind each bar, "?" or not, worked out from the others. Used
// only for the lengths of the bars, never written or spoken.
export function partWholeSizes(spec: PartWholeSpec): {
  whole: number;
  parts: number[];
} | null {
  const known = spec.parts.filter(
    (part): part is number => typeof part === 'number',
  );
  const sum = known.reduce((total, part) => total + part, 0);
  if (spec.whole === '?') {
    if (known.length !== spec.parts.length || sum > MAX_VALUE) return null;
    return { whole: sum, parts: known };
  }
  const whole = spec.whole;
  if (known.length === spec.parts.length) {
    return sum === whole ? { whole, parts: known } : null;
  }
  const missing = whole - sum;
  if (missing < 1) return null;
  return {
    whole,
    parts: spec.parts.map((part) => (part === '?' ? missing : part)),
  };
}

export function compareSizes(
  spec: CompareSpec,
): { longer: number; shorter: number } | null {
  const [top, bottom] = spec.bars;
  const gap = spec.difference;
  if (typeof top.value === 'number' && typeof bottom.value === 'number') {
    if (top.value <= bottom.value) return null;
    if (typeof gap === 'number' && gap !== top.value - bottom.value) {
      return null;
    }
    return { longer: top.value, shorter: bottom.value };
  }
  // One bar is "?", so the gap must be known to size it.
  if (typeof gap !== 'number') return null;
  if (top.value === '?' && typeof bottom.value === 'number') {
    const longer = bottom.value + gap;
    return longer > MAX_VALUE ? null : { longer, shorter: bottom.value };
  }
  if (typeof top.value === 'number') {
    const shorter = top.value - gap;
    return shorter < 1 ? null : { longer: top.value, shorter };
  }
  return null;
}

function readPartWhole(record: Record<string, unknown>): PartWholeSpec | null {
  const whole = readValue(record.whole);
  const { parts } = record;
  if (whole === null || !Array.isArray(parts)) return null;
  if (parts.length < 2 || parts.length > 3) return null;
  const read: BarValue[] = [];
  for (const part of parts as unknown[]) {
    const value = readValue(part);
    if (value === null) return null;
    read.push(value);
  }
  // One unknown at most, or the picture has more than one answer.
  if (unknowns([whole, ...read]) > 1) return null;
  const spec: PartWholeSpec = {
    type: 'bar_model',
    kind: 'part_whole',
    whole,
    parts: read,
  };
  return partWholeSizes(spec) === null ? null : spec;
}

function readCompare(record: Record<string, unknown>): CompareSpec | null {
  const { bars } = record;
  if (!Array.isArray(bars) || bars.length !== 2) return null;
  const read: CompareBar[] = [];
  for (const entry of bars as unknown[]) {
    if (typeof entry !== 'object' || entry === null) return null;
    const bar = entry as Record<string, unknown>;
    const value = readValue(bar.value);
    const name = readName(bar.name);
    if (value === null || name === undefined) return null;
    read.push({ name, value });
  }
  const [top, bottom] = read;
  if (top === undefined || bottom === undefined) return null;
  // Both bars named, or neither.
  if ((top.name === null) !== (bottom.name === null)) return null;
  let difference: BarValue | null = null;
  if (record.difference !== undefined) {
    difference = readValue(record.difference);
    if (difference === null) return null;
  }
  if (unknowns([top.value, bottom.value, difference]) > 1) return null;
  const spec: CompareSpec = {
    type: 'bar_model',
    kind: 'compare',
    bars: [top, bottom],
    difference,
  };
  return compareSizes(spec) === null ? null : spec;
}

// A whole with parts, or two bars to compare: never both.
export function readBarModel(
  record: Record<string, unknown>,
): BarModelSpec | null {
  const partWhole = record.whole !== undefined || record.parts !== undefined;
  const compare = record.bars !== undefined;
  if (partWhole === compare) return null;
  return partWhole ? readPartWhole(record) : readCompare(record);
}

function say(value: BarValue): string {
  return value === '?' ? 'a question mark' : String(value);
}

function list(words: string[]): string {
  return words.length < 2
    ? (words[0] ?? '')
    : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1] ?? ''}`;
}

// What a screen reader is told in place of the picture: the numbers printed
// on the bars and where the "?" is, never the number behind it.
export function describeBarModel(spec: BarModelSpec): string {
  if (spec.kind === 'part_whole') {
    return `A bar model. The whole bar is ${say(spec.whole)}. Under it are ${String(spec.parts.length)} parts: ${list(spec.parts.map(say))}.`;
  }
  const [top, bottom] = spec.bars;
  const bar = (where: string, entry: CompareBar) =>
    entry.name === null
      ? `The ${where} bar is ${say(entry.value)}.`
      : `The ${where} bar, ${entry.name}, is ${say(entry.value)}.`;
  const gap =
    spec.difference === null
      ? ''
      : ` The difference is ${say(spec.difference)}.`;
  return `A bar model with two bars to compare. ${bar('top', top)} ${bar('bottom', bottom)}${gap}`;
}

// What follows is where everything is drawn. The units are close to one
// pixel each when the picture is shown at its natural width.

export const BAR_LABEL_SIZE = 15;
export const BAR_NAME_SIZE = 13;
const EDGE = 8;
const BAR_HEIGHT = 40;
const ROW_GAP = 14;
const AREA = 300;
// A digit is about this wide at BAR_LABEL_SIZE, plus a little room either
// side, to tell whether a number fits inside its bar.
const DIGIT_WIDTH = 9;
const LABEL_ROOM = 8;
// The extra row under the bars for a number that does not fit inside one.
const BELOW_ROW = 22;
const NAME_WIDTH = 7.5;

export type BarBox = {
  role: 'whole' | 'part' | 'longer' | 'shorter' | 'difference';
  value: BarValue;
  x: number;
  y: number;
  width: number;
  height: number;
  // True when the bar is too short for its number, which is then printed
  // just under the bar instead of in it.
  labelBelow: boolean;
  // Where the middle of the number sits, in it or under it.
  labelY: number;
};

export type BarModelPicture = {
  bars: BarBox[];
  names: { text: string; x: number; y: number }[];
  width: number;
  height: number;
};

function round(value: number): number {
  return Number(value.toFixed(2));
}

function bar(
  role: BarBox['role'],
  value: BarValue,
  x: number,
  y: number,
  width: number,
): BarBox {
  const labelBelow = width < String(value).length * DIGIT_WIDTH + LABEL_ROOM;
  return {
    role,
    value,
    x: round(x),
    y,
    width: round(width),
    height: BAR_HEIGHT,
    labelBelow,
    labelY: labelBelow
      ? y + BAR_HEIGHT + BELOW_ROW / 2 + 2
      : y + BAR_HEIGHT / 2,
  };
}

function pictureOf(
  bars: BarBox[],
  names: BarModelPicture['names'],
): BarModelPicture {
  const below = bars.some((entry) => entry.labelBelow);
  return {
    bars,
    names,
    width: AREA + EDGE * 2,
    height: EDGE * 2 + BAR_HEIGHT * 2 + ROW_GAP + (below ? BELOW_ROW : 0),
  };
}

export function partWholePicture(spec: PartWholeSpec): BarModelPicture {
  const sizes = partWholeSizes(spec) ?? {
    whole: spec.parts.length,
    parts: spec.parts.map(() => 1),
  };
  const bars: BarBox[] = [bar('whole', spec.whole, EDGE, EDGE, AREA)];
  // Each part is drawn to scale, and the parts fill the whole exactly.
  let x = EDGE;
  spec.parts.forEach((value, index) => {
    const last = index === spec.parts.length - 1;
    const width = last
      ? EDGE + AREA - x
      : ((sizes.parts[index] ?? 0) / sizes.whole) * AREA;
    bars.push(bar('part', value, x, EDGE + BAR_HEIGHT + ROW_GAP, width));
    x += width;
  });
  return pictureOf(bars, []);
}

export function comparePicture(spec: CompareSpec): BarModelPicture {
  const sizes = compareSizes(spec) ?? { longer: 2, shorter: 1 };
  const [top, bottom] = spec.bars;
  const longestName = Math.max(
    (top.name ?? '').length,
    (bottom.name ?? '').length,
  );
  const nameWidth =
    longestName === 0 ? 0 : Math.ceil(longestName * NAME_WIDTH) + 10;
  const left = EDGE + nameWidth;
  const area = AREA - nameWidth;
  // The shorter bar is drawn to scale against the longer one.
  const shorter = round(area * (sizes.shorter / sizes.longer));
  const topY = EDGE;
  const bottomY = EDGE + BAR_HEIGHT + ROW_GAP;
  const bars: BarBox[] = [
    bar('longer', top.value, left, topY, area),
    bar('shorter', bottom.value, left, bottomY, shorter),
  ];
  if (spec.difference !== null) {
    bars.push(
      bar(
        'difference',
        spec.difference,
        left + shorter,
        bottomY,
        area - shorter,
      ),
    );
  }
  const names =
    top.name === null || bottom.name === null
      ? []
      : [
          { text: top.name, x: EDGE, y: topY + BAR_HEIGHT / 2 },
          { text: bottom.name, x: EDGE, y: bottomY + BAR_HEIGHT / 2 },
        ];
  return pictureOf(bars, names);
}

export function barModelPicture(spec: BarModelSpec): BarModelPicture {
  return spec.kind === 'part_whole'
    ? partWholePicture(spec)
    : comparePicture(spec);
}
